import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Archive, 
  Download, 
  Trash2, 
  FileArchive, 
  Calendar, 
  User, 
  Mail,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  HardDrive
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DocumentArchives() {
  const { user } = useAuth();
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedArchive, setSelectedArchive] = useState(null);

  const fetchArchives = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/archives`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch archives');
      
      const data = await response.json();
      setArchives(data.archives || []);
    } catch (error) {
      console.error('Error fetching archives:', error);
      toast.error('Failed to load archives');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArchives();
  }, []);

  const handleDownload = async (filename) => {
    setDownloading(filename);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/archives/${encodeURIComponent(filename)}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to download archive');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename.replace('.enc', '');
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Archive downloaded successfully');
    } catch (error) {
      console.error('Error downloading archive:', error);
      toast.error('Failed to download archive');
    } finally {
      setDownloading(null);
    }
  };

  const handleDeleteClick = (archive) => {
    setSelectedArchive(archive);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedArchive) return;
    
    setDeleting(selectedArchive.filename);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/archives/${encodeURIComponent(selectedArchive.filename)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to delete archive');
      
      toast.success('Archive deleted successfully');
      setArchives(prev => prev.filter(a => a.filename !== selectedArchive.filename));
    } catch (error) {
      console.error('Error deleting archive:', error);
      toast.error('Failed to delete archive');
    } finally {
      setDeleting(null);
      setShowDeleteModal(false);
      setSelectedArchive(null);
    }
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalSize = () => {
    const totalBytes = archives.reduce((sum, a) => sum + a.size_bytes, 0);
    return (totalBytes / (1024 * 1024)).toFixed(2);
  };

  return (
    <div className="space-y-6" data-testid="document-archives-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Archive className="h-6 w-6 text-orange-500" />
            Document Archives
          </h1>
          <p className="text-gray-600 mt-1">
            Encrypted archives of approved driver documents
          </p>
        </div>
        <Button 
          onClick={fetchArchives} 
          variant="outline"
          disabled={loading}
          data-testid="refresh-archives-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <FileArchive className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Archives</p>
                <p className="text-2xl font-bold">{archives.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <HardDrive className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Size</p>
                <p className="text-2xl font-bold">{getTotalSize()} MB</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Encryption</p>
                <p className="text-2xl font-bold">AES-256</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Archives List */}
      <Card>
        <CardHeader>
          <CardTitle>Archived Documents</CardTitle>
          <CardDescription>
            Download decrypted ZIP files or delete archives no longer needed
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-orange-500" />
            </div>
          ) : archives.length === 0 ? (
            <div className="text-center py-12">
              <Archive className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No archives yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Archives are created when driver applications are approved
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {archives.map((archive) => (
                <div 
                  key={archive.filename}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-gray-50 rounded-lg border gap-4"
                  data-testid={`archive-item-${archive.filename}`}
                >
                  <div className="flex items-start gap-4 flex-1">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <FileArchive className="h-5 w-5 text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {archive.filename.replace('.enc', '')}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDate(archive.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <HardDrive className="h-3.5 w-3.5" />
                          {archive.size_mb} MB
                        </span>
                      </div>
                      {archive.application && (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm">
                          <span className="flex items-center gap-1 text-gray-600">
                            <User className="h-3.5 w-3.5" />
                            {archive.application.full_name}
                          </span>
                          <span className="flex items-center gap-1 text-gray-500">
                            <Mail className="h-3.5 w-3.5" />
                            {archive.application.email}
                          </span>
                          <Badge variant={archive.application.status === 'approved' ? 'success' : 'secondary'}>
                            {archive.application.status}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(archive.filename)}
                      disabled={downloading === archive.filename}
                      className="flex-1 sm:flex-none"
                      data-testid={`download-archive-${archive.filename}`}
                    >
                      {downloading === archive.filename ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      Download
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteClick(archive)}
                      disabled={deleting === archive.filename}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-archive-${archive.filename}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedArchive && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full" data-testid="delete-archive-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Archive</h3>
            </div>
            
            <p className="text-gray-600 mb-4">
              Are you sure you want to permanently delete this archive?
            </p>
            
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <p className="font-medium text-gray-900 truncate">
                {selectedArchive.filename.replace('.enc', '')}
              </p>
              {selectedArchive.application && (
                <p className="text-sm text-gray-500 mt-1">
                  Driver: {selectedArchive.application.full_name}
                </p>
              )}
            </div>
            
            <p className="text-sm text-red-600 mb-6">
              This action cannot be undone. The encrypted archive file will be permanently removed.
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedArchive(null);
                }}
                className="flex-1"
                data-testid="cancel-delete-archive"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-archive"
              >
                {deleting ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Delete Archive
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
